<template>
    <b-modal
        id="modal-wi-comments"
        ref="commentsModal"
        size="xl"
        centered
        scrollable
        hide-footer
        no-close-on-backdrop
        data-test="modal-wi-comments"
        :data-state="loadingComments ? 'loading' : 'idle'"
        :title="modalTitle"
        header-class="wi-comments-header py-2 px-3"
        body-class="wi-comments-body p-3"
        @hidden="onModalHidden"
    >
        <template #modal-header="{ close }">
            <div class="d-flex align-items-center justify-content-between w-100 pr-1 modal-header-content">
                <div class="d-flex align-items-center flex-grow-1 min-w-0 mr-3 overflow-hidden">
                    <nav-link-button
                        v-if="activeItem"
                        :href="activeItemAdoUrl"
                        :id="activeItem.workItemId"
                        :type-icon="activeItem.workItemTypeIcon"
                        :work-item-type="activeItem.workItemType"
                        prefix="#"
                        title="Open work item in Azure DevOps"
                        class="mr-2 flex-shrink-0"
                        data-test="nav-link-wi-modal"
                    />
                    <span
                        class="text-truncate mr-2 font-weight-bold wi-modal-title flex-grow-1"
                        :title="activeItem ? activeItem.title : ''"
                    >
                        {{ activeItem && activeItem.title ? activeItem.title : 'Work Item Discussion' }}
                    </span>
                    <status-badge v-if="activeItem && activeItem.state" :status="activeItem.state" class="flex-shrink-0" />
                    <b-badge
                        v-if="activeItem && activeItem.discussionStatus === 'AwaitingAnswer'"
                        variant="danger"
                        class="ml-2 py-1 pl-2 pr-1 font-weight-bold d-inline-flex align-items-center flex-shrink-0"
                        data-test="badge-modal-awaiting-answer"
                    >
                        <i class="bi bi-exclamation-circle-fill mr-1"></i>Awaiting Answer
                        <button
                            type="button"
                            class="badge-dismiss-btn ml-1"
                            title="Dismiss label (answered in person or meeting)"
                            aria-label="Dismiss label"
                            data-test="btn-modal-dismiss-status"
                            @click.stop.prevent="dismissModalStatus"
                        >
                            <i class="bi bi-x"></i>
                        </button>
                    </b-badge>
                    <b-badge
                        v-else-if="activeItem && activeItem.discussionStatus === 'AwaitingReply'"
                        variant="warning"
                        class="ml-2 py-1 pl-2 pr-1 font-weight-bold d-inline-flex align-items-center flex-shrink-0"
                        data-test="badge-modal-awaiting-reply"
                    >
                        <i class="bi bi-hourglass-split mr-1"></i>Awaiting Reply
                        <button
                            type="button"
                            class="badge-dismiss-btn ml-1"
                            title="Dismiss label (replied in person or meeting)"
                            aria-label="Dismiss label"
                            data-test="btn-modal-dismiss-status"
                            @click.stop.prevent="dismissModalStatus"
                        >
                            <i class="bi bi-x"></i>
                        </button>
                    </b-badge>
                    <b-badge
                        v-else-if="activeItem && activeItem.discussionStatus === 'Replied'"
                        variant="success"
                        class="ml-2 py-1 pl-2 pr-1 font-weight-bold d-inline-flex align-items-center flex-shrink-0"
                        data-test="badge-modal-replied"
                    >
                        <i class="bi bi-check-circle-fill mr-1"></i>Replied
                        <button
                            type="button"
                            class="badge-dismiss-btn ml-1"
                            title="Dismiss label"
                            aria-label="Dismiss label"
                            data-test="btn-modal-dismiss-status"
                            @click.stop.prevent="dismissModalStatus"
                        >
                            <i class="bi bi-x"></i>
                        </button>
                    </b-badge>
                    <b-badge
                        v-else-if="activeItem && activeItem.discussionStatus === 'Answered'"
                        variant="success"
                        class="ml-2 py-1 pl-2 pr-1 font-weight-bold d-inline-flex align-items-center flex-shrink-0"
                        data-test="badge-modal-answered"
                    >
                        <i class="bi bi-check-circle-fill mr-1"></i>Answered
                        <button
                            type="button"
                            class="badge-dismiss-btn ml-1"
                            title="Dismiss label"
                            aria-label="Dismiss label"
                            data-test="btn-modal-dismiss-status"
                            @click.stop.prevent="dismissModalStatus"
                        >
                            <i class="bi bi-x"></i>
                        </button>
                    </b-badge>
                </div>
                <div class="d-flex align-items-center flex-shrink-0">
                    <b-button
                        size="sm"
                        variant="link"
                        class="text-muted p-0"
                        aria-label="Close discussion"
                        title="Close"
                        data-test="btn-close-comments"
                        v-b-popover.hover.top.noninteractive="actionHoverPop('Close')"
                        @click="close"
                    >
                        <i class="bi bi-x-lg"></i>
                    </b-button>
                </div>
            </div>
        </template>

        <!-- View-Only notice when on Free tier -->
        <div v-if="isViewOnlyMode" class="alert alert-warning d-flex align-items-center justify-content-between p-2 mb-3" data-test="alert-discussion-view-only">
            <div class="small">
                <i class="bi bi-lock-fill mr-1"></i>
                Replying to discussions directly from VelocyTrack requires the <strong>Pro Plan</strong>.
            </div>
            <b-button size="sm" variant="warning" class="font-weight-bold text-dark ml-2 shadow-sm"
                data-test="btn-upgrade-discussion"
                @click="onUpgradeClick">
                <i class="bi bi-arrow-up-circle mr-1"></i>Upgrade Plan
            </b-button>
        </div>

        <!-- Reply Composer (on top of all messages) -->
        <div v-if="!isViewOnlyMode" class="wi-reply-composer mb-3 p-3 rounded border shadow-sm position-relative" data-test="container-new-comment">
            <b-form @submit.prevent="submitComment">
                <div class="d-flex align-items-center justify-content-between mb-2">
                    <span class="font-weight-bold small text-muted">
                        <i class="bi bi-chat-left-text-fill text-primary mr-1"></i>New Comment
                    </span>
                    <span class="extra-small text-muted">
                        <span v-if="teamMembers.length > 0" class="mr-2 text-info d-none d-sm-inline">
                            <i class="bi bi-at"></i>Type <strong>@</strong> to mention
                        </span>
                        <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to reply
                    </span>
                </div>

                <div class="position-relative">
                    <b-form-textarea
                        ref="commentTextarea"
                        v-model="newCommentText"
                        placeholder="Write a comment or reply to this discussion... Type @ to mention anyone (Ctrl+Enter to post)"
                        rows="2"
                        max-rows="6"
                        no-resize
                        :disabled="postingComment"
                        class="comment-textarea mb-2"
                        data-test="input-new-comment"
                        @input="onTextareaInput"
                        @keydown="onTextareaKeydown"
                        @click="onTextareaInput"
                        @blur="onTextareaBlur"
                    />

                    <!-- Typeahead Mention Popup (below cursor, up to 5, no scroll) -->
                    <div
                        v-if="showMentionDropdown && (filteredTeamMembers.length > 0 || searchingIdentities || (mentionQuery && mentionQuery.length > 0))"
                        class="mention-typeahead-dropdown shadow-lg rounded border"
                        :style="dropdownPositionStyle"
                        data-test="dropdown-mention-typeahead"
                    >
                        <div v-if="!searchingIdentities && filteredTeamMembers.length === 0" class="p-2 text-center text-muted extra-small" data-test="mention-empty-state">
                            No members found matching "@{{ mentionQuery }}"
                        </div>
                        <div v-else class="mention-list-container" role="listbox">
                            <div
                                v-for="(member, idx) in filteredTeamMembers"
                                :key="member.id || member.uniqueName || idx"
                                class="mention-member-item d-flex align-items-center px-3 py-2"
                                :class="{ 'is-selected': idx === selectedMentionIndex, 'is-team-mate': member.isTeamMember }"
                                :data-test="`mention-option-${idx}`"
                                :data-test-id="member.id"
                                role="option"
                                :aria-selected="idx === selectedMentionIndex"
                                @mousedown.prevent="selectMention(member)"
                                @mouseenter="selectedMentionIndex = idx"
                            >
                                <div class="mention-avatar-wrapper mr-2 flex-shrink-0">
                                    <img
                                        v-if="member.imageUrl && !member.avatarFailed"
                                        :src="member.imageUrl"
                                        class="rounded-circle mention-member-avatar"
                                        :alt="member.displayName"
                                        @error="onAvatarError($event, member)"
                                    />
                                    <div
                                        v-else
                                        class="avatar-fallback rounded-circle mention-member-avatar"
                                        :style="getAvatarBgStyle(member.displayName)"
                                    >
                                        {{ getInitials(member.displayName) }}
                                    </div>
                                </div>
                                <div class="d-flex flex-column min-w-0 flex-grow-1 text-truncate">
                                    <div class="d-flex align-items-center">
                                        <span class="font-weight-bold text-truncate member-name">{{ member.displayName }}</span>
                                        <span
                                            v-if="member.isTeamMember"
                                            class="team-mate-badge ml-2"
                                            title="Team member (Mate)"
                                            data-test="badge-team-member"
                                        >
                                            <i class="bi bi-people-fill mr-1"></i>Team
                                        </span>
                                    </div>
                                    <span v-if="member.uniqueName" class="extra-small text-truncate member-handle">{{ member.uniqueName }}</span>
                                </div>
                                <b-badge v-if="member.isCurrentUser" variant="primary" class="extra-small ml-2 flex-shrink-0">You</b-badge>
                            </div>
                            <div v-if="searchingIdentities" class="px-2 py-1 text-center text-muted extra-small border-top" data-test="mention-searching-state">
                                <b-spinner small class="mr-1"></b-spinner>Searching all members...
                            </div>
                        </div>
                    </div>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                    <span class="text-muted extra-small">
                        <i class="bi bi-cloud-arrow-up mr-1 text-secondary"></i>Synced with Azure DevOps
                    </span>
                    <b-button
                        type="submit"
                        variant="primary"
                        size="sm"
                        :disabled="postingComment || !newCommentText.trim()"
                        class="px-3 shadow-sm"
                        data-test="btn-submit-comment"
                        :data-state="postingComment ? 'loading' : 'idle'"
                    >
                        <b-spinner v-if="postingComment" small class="mr-1"></b-spinner>
                        <i v-else class="bi bi-send-fill mr-1"></i>Reply
                    </b-button>
                </div>
            </b-form>
        </div>

        <!-- Loading State -->
        <div v-if="loadingComments" class="py-4">
            <load-spinner size="sm" text="Loading discussion comments..." />
        </div>

        <!-- Empty State -->
        <div v-else-if="comments.length === 0" class="text-center py-4 text-muted empty-comments-card">
            <i class="bi bi-chat-left-dots text-secondary mb-2" style="font-size: 2rem; display: block;"></i>
            <div class="font-weight-bold mb-1">No comments yet</div>
            <small>Start the discussion above. All comments are synced with Azure DevOps.</small>
        </div>

        <!-- Comments Stream (Newest on top) -->
        <div v-else class="comments-stream-container">
            <div class="d-flex align-items-center justify-content-between mb-2 text-muted px-1">
                <span class="font-weight-bold small">
                    <i class="bi bi-chat-dots mr-1"></i>Discussion ({{ sortedComments.length }})
                </span>
                <span class="extra-small text-muted">
                    <i class="bi bi-sort-down mr-1"></i>Newest on top
                </span>
            </div>

            <div class="comments-stream">
                <div
                    v-for="comment in sortedComments"
                    :key="comment.id"
                    class="comment-bubble-wrapper mb-3 d-flex align-items-start"
                    data-test="comment-bubble"
                    :data-test-id="comment.id"
                >
                    <div class="comment-avatar mr-2 flex-shrink-0">
                        <b-img
                            v-if="comment.authorAvatar"
                            :src="comment.authorAvatar"
                            class="rounded-circle avatar-img"
                            @error="onAvatarError"
                        />
                        <div v-else class="avatar-fallback rounded-circle">
                            {{ getInitials(comment.authorName) }}
                        </div>
                    </div>

                    <div
                        class="comment-bubble flex-grow-1 p-2 px-3 rounded border"
                        :class="{ 'has-mention-to-me': comment.mentionsMe }"
                    >
                        <div class="d-flex align-items-center justify-content-between mb-1">
                            <div class="d-flex align-items-center flex-wrap">
                                <strong class="comment-author-name small mr-1">{{ comment.authorName }}</strong>
                                <b-badge v-if="comment.isFromCurrentUser" variant="light" class="extra-small ml-1 text-muted border">You</b-badge>
                            </div>
                            <div class="d-flex align-items-center">
                                <div v-if="!isViewOnlyMode && comment.isFromCurrentUser && editingCommentId !== comment.id && confirmingDeleteCommentId !== comment.id" class="comment-item-actions d-inline-flex align-items-center mr-2">
                                    <b-button
                                        size="sm"
                                        variant="link"
                                        class="p-0 px-1 text-secondary comment-action-btn"
                                        data-test="btn-edit-comment"
                                        aria-label="Edit comment"
                                        title="Edit comment"
                                        @click="startEditComment(comment)"
                                    >
                                        <i class="bi bi-pencil mr-1"></i>Edit
                                    </b-button>
                                    <span class="text-muted mx-1 extra-small">|</span>
                                    <b-button
                                        size="sm"
                                        variant="link"
                                        class="p-0 px-1 text-danger comment-action-btn"
                                        data-test="btn-delete-comment"
                                        aria-label="Delete comment"
                                        title="Delete comment"
                                        @click="promptDeleteComment(comment)"
                                    >
                                        <i class="bi bi-trash mr-1"></i>Delete
                                    </b-button>
                                </div>
                                <span class="comment-date text-muted extra-small" :title="comment.createdDate">
                                    {{ formatCommentDate(comment.createdDate) }}
                                </span>
                            </div>
                        </div>
                        <div v-if="editingCommentId === comment.id" class="comment-edit-box mt-2" data-test="container-edit-comment">
                            <b-form-textarea
                                ref="editCommentTextarea"
                                v-model="editCommentDraft"
                                rows="2"
                                max-rows="6"
                                size="sm"
                                :disabled="savingCommentEdit"
                                class="comment-textarea mb-2"
                                data-test="input-edit-comment"
                                placeholder="Update your comment... (Ctrl+Enter to save, Esc to cancel)"
                                @keydown="onEditCommentKeydown($event, comment)"
                            />
                            <div class="d-flex justify-content-end align-items-center">
                                <b-button
                                    size="sm"
                                    variant="outline-secondary"
                                    class="mr-2"
                                    :disabled="savingCommentEdit"
                                    data-test="btn-cancel-comment-edit"
                                    @click="cancelEditComment"
                                >
                                    Cancel
                                </b-button>
                                <b-button
                                    size="sm"
                                    variant="primary"
                                    :disabled="savingCommentEdit || !editCommentDraft.trim()"
                                    :data-state="savingCommentEdit ? 'loading' : 'idle'"
                                    data-test="btn-save-comment-edit"
                                    @click="saveEditComment(comment)"
                                >
                                    <b-spinner v-if="savingCommentEdit" small class="mr-1"></b-spinner>
                                    <i v-else class="bi bi-check2 mr-1"></i>Save
                                </b-button>
                            </div>
                        </div>
                        <div v-else-if="confirmingDeleteCommentId === comment.id" class="comment-delete-confirm-box mt-2 p-2 rounded border border-danger d-flex align-items-center justify-content-between flex-wrap" data-test="container-confirm-delete-comment">
                            <span class="small text-danger font-weight-bold mr-2">
                                <i class="bi bi-exclamation-triangle-fill mr-1"></i>Delete this comment permanently?
                            </span>
                            <div class="d-flex align-items-center mt-1 mt-sm-0">
                                <b-button
                                    size="sm"
                                    variant="outline-secondary"
                                    class="mr-2 py-0 px-2"
                                    :disabled="deletingComment"
                                    data-test="btn-cancel-delete-comment"
                                    @click="cancelDeleteComment"
                                >
                                    Cancel
                                </b-button>
                                <b-button
                                    size="sm"
                                    variant="danger"
                                    class="py-0 px-2"
                                    :disabled="deletingComment"
                                    :data-state="deletingComment ? 'loading' : 'idle'"
                                    data-test="btn-confirm-delete-comment"
                                    @click="confirmDeleteComment(comment)"
                                >
                                    <b-spinner v-if="deletingComment" small class="mr-1"></b-spinner>
                                    <i v-else class="bi bi-trash-fill mr-1"></i>Delete
                                </b-button>
                            </div>
                        </div>
                        <div v-else class="comment-content small" v-html="renderCommentText(comment.text, comment.mentionsMe)"></div>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import DOMPurify from 'dompurify';
import NavLinkButton from './nav-link-button.vue';
import StatusBadge from './status-badge.vue';
import mixinToast from '../mixinToast';
import commonMixin from '../mixinCommon';
import mixinAccount from '../mixinAccount';

export default {
    name: 'ModalWorkItemComments',
    components: {
        'nav-link-button': NavLinkButton,
        'status-badge': StatusBadge
    },
    mixins: [mixinToast, commonMixin, mixinAccount],
    data() {
        return {
            activeItem: null,
            comments: [],
            newCommentText: '',
            loadingComments: false,
            postingComment: false,
            teamMembers: [],
            searchingIdentities: false,
            identitySearchTimer: null,
            showMentionDropdown: false,
            mentionQuery: '',
            mentionStartIndex: -1,
            selectedMentionIndex: 0,
            knownMentions: [],


            caretTop: 36,
            caretLeft: 0,
            editingCommentId: null,
            editCommentDraft: '',
            savingCommentEdit: false,
            confirmingDeleteCommentId: null,
            deletingComment: false
        };
    },
    computed: {
        modalTitle() {
            if (!this.activeItem) return 'Work Item Discussion';
            return `Work Item #${this.activeItem.workItemId} — ${this.activeItem.title || 'Discussion'}`;
        },
        activeItemAdoUrl() {
            if (!this.activeItem) return null;
            if (this.activeItem.url && this.activeItem.url !== '#') return this.activeItem.url;
            if (this.activeItem.workItemId) {
                const project = this.activeItem.project || this.activeItem.projectName || this.globalProject?.name || 'core';
                return `https://dev.azure.com/velocytrack/${encodeURIComponent(project)}/_workitems/edit/${this.activeItem.workItemId}`;
            }
            return null;
        },
        sortedComments() {
            if (!this.comments || !this.comments.length) return [];
            return [...this.comments].sort((a, b) => {
                const da = a.createdDate ? new Date(a.createdDate).getTime() : 0;
                const db = b.createdDate ? new Date(b.createdDate).getTime() : 0;
                if (db !== da) return db - da; // newest first
                return (b.id || 0) - (a.id || 0);
            });
        },
        dropdownPositionStyle() {
            return {
                top: `${this.caretTop}px`,
                left: `${this.caretLeft}px`
            };
        },
        filteredTeamMembers() {
            if (!this.teamMembers || this.teamMembers.length === 0) return [];
            const q = (this.mentionQuery || '').toLowerCase().trim();
            const seen = new Set();
            const list = [];
            for (const m of this.teamMembers) {
                if (!m) continue;
                const key = (m.uniqueName || m.displayName || m.id || '').toLowerCase().trim();
                if (!key || seen.has(key)) continue;
                if (!q || (m.displayName && m.displayName.toLowerCase().includes(q)) ||
                          (m.uniqueName && m.uniqueName.toLowerCase().includes(q))) {
                    seen.add(key);
                    list.push(m);
                }
            }
            list.sort((a, b) => {
                if (a.isTeamMember !== b.isTeamMember) return a.isTeamMember ? -1 : 1;
                if (a.isCurrentUser !== b.isCurrentUser) return a.isCurrentUser ? -1 : 1;
                return (a.displayName || '').localeCompare(b.displayName || '');
            });
            return list.slice(0, 5);
        }
    },
    methods: {
        open(item) {
            this.activeItem = item;
            this.comments = [];
            this.newCommentText = '';
            this.showMentionDropdown = false;
            this.mentionQuery = '';
            this.mentionStartIndex = -1;
            this.selectedMentionIndex = 0;
            this.knownMentions = [];
            this.editingCommentId = null;
            this.editCommentDraft = '';
            this.savingCommentEdit = false;
            this.confirmingDeleteCommentId = null;
            this.deletingComment = false;
            this.searchingIdentities = false;
            if (this.identitySearchTimer) {
                clearTimeout(this.identitySearchTimer);
                this.identitySearchTimer = null;
            }
            this.loadingComments = true;
            this.$bvModal.show('modal-wi-comments');
            this.loadComments(item.workItemId);
            this.loadTeamMembers();
        },
        show(item) {
            this.open(item);
        },
        async loadTeamMembers() {
            try {
                const projectId = this.activeItem?.projectId || this.activeItem?.project || this.globalProjectId;
                const members = await this.$store.dispatch('searchIdentitiesAsync', { projectId });
                this.teamMembers = Array.isArray(members) ? [...members] : [];
            } catch (_) {
                this.teamMembers = [];
            }
        },
        async searchRemoteIdentities(query) {
            const q = (query || '').trim();
            if (!q) return;
            const projectId = this.activeItem?.projectId || this.activeItem?.project || this.globalProjectId;
            this.searchingIdentities = true;
            try {
                const results = await this.$store.dispatch('searchIdentitiesAsync', { query: q, projectId, force: true });
                if (Array.isArray(results) && results.length > 0) {
                    const existingMap = new Map((this.teamMembers || []).map(m => [
                        (m.uniqueName || m.displayName || m.id || '').toLowerCase().trim(),
                        m
                    ]));
                    for (const r of results) {
                        const key = (r.uniqueName || r.displayName || r.id || '').toLowerCase().trim();
                        if (existingMap.has(key)) {
                            const existing = existingMap.get(key);
                            if (r.isTeamMember) this.$set(existing, 'isTeamMember', true);
                            if (!existing.imageUrl && r.imageUrl) this.$set(existing, 'imageUrl', r.imageUrl);
                            if (r.descriptor && !existing.descriptor) this.$set(existing, 'descriptor', r.descriptor);
                        } else {
                            this.teamMembers.push(r);
                            existingMap.set(key, r);
                        }
                    }
                }
            } catch (_) {
                // Ignore search error
            } finally {
                this.searchingIdentities = false;
            }
        },
        getTextareaEl() {
            if (!this.$refs.commentTextarea) return null;
            return this.$refs.commentTextarea.$el ? this.$refs.commentTextarea.$el : this.$refs.commentTextarea;
        },
        onTextareaInput() {
            this.$nextTick(() => {
                const el = this.getTextareaEl();
                if (!el) {
                    this.showMentionDropdown = false;
                    return;
                }
                const cursorPos = el.selectionStart;
                const textBeforeCursor = this.newCommentText.slice(0, cursorPos);
                const atMatch = textBeforeCursor.match(/(?:^|\s)@([^\s@]*)$/);
                if (atMatch) {
                    this.mentionQuery = atMatch[1];
                    this.mentionStartIndex = textBeforeCursor.lastIndexOf('@' + atMatch[1]);
                    this.selectedMentionIndex = 0;
                    this.showMentionDropdown = true;
                    this.updateDropdownPosition();

                    if (this.identitySearchTimer) {
                        clearTimeout(this.identitySearchTimer);
                    }
                    if (this.mentionQuery && this.mentionQuery.length >= 2) {
                        this.identitySearchTimer = setTimeout(() => {
                            this.searchRemoteIdentities(this.mentionQuery);
                        }, 250);
                    }
                } else {
                    this.showMentionDropdown = false;
                }
            });
        },
        onTextareaKeydown(event) {
            if (this.showMentionDropdown && event.key === 'Escape') {
                event.preventDefault();
                this.showMentionDropdown = false;
                return;
            }

            if (this.showMentionDropdown && this.filteredTeamMembers.length > 0) {
                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    this.selectedMentionIndex = (this.selectedMentionIndex + 1) % this.filteredTeamMembers.length;
                    return;
                }
                if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    this.selectedMentionIndex = (this.selectedMentionIndex - 1 + this.filteredTeamMembers.length) % this.filteredTeamMembers.length;
                    return;
                }
                if (event.key === 'Enter' || event.key === 'Tab') {
                    if (!event.ctrlKey) {
                        event.preventDefault();
                        const chosen = this.filteredTeamMembers[this.selectedMentionIndex];
                        if (chosen) {
                            this.selectMention(chosen);
                        }
                        return;
                    }
                }
            }

            if (event.ctrlKey && event.key === 'Enter') {
                event.preventDefault();
                this.submitComment();
            }
        },
        onTextareaBlur() {
            setTimeout(() => {
                this.showMentionDropdown = false;
            }, 200);
        },
        selectMention(member) {
            const el = this.getTextareaEl();
            const cursorPos = el ? el.selectionStart : this.newCommentText.length;
            const before = this.newCommentText.slice(0, this.mentionStartIndex);
            const after = this.newCommentText.slice(cursorPos);
            const inserted = `@${member.displayName} `;
            this.newCommentText = before + inserted + after;
            this.showMentionDropdown = false;

            if (!this.knownMentions.some(m => m.id === member.id)) {
                this.knownMentions.push(member);
            }

            this.$nextTick(() => {
                if (el) {
                    el.focus();
                    const newPos = before.length + inserted.length;
                    el.setSelectionRange(newPos, newPos);
                }
            });
        },
        async loadComments(workItemId) {
            try {
                const resp = await this.$http.get('/TaskManagement/GetWorkItemComments', {
                    params: { workItemId }
                });
                this.comments = (Array.isArray(resp.data) ? resp.data : []).map(c => ({
                    ...c,
                    isFromCurrentUser: !!(c.isFromCurrentUser || this.isAuthoredByCurrentUser(c))
                }));
                const mentionCount = this.comments.filter(c => c.mentionsMe).length;
                if (this.activeItem && this.activeItem.workItemId === workItemId) {
                    this.$set(this.activeItem, 'commentCount', this.comments.length);
                    this.$set(this.activeItem, 'mentionCount', mentionCount);
                }
                this.$emit('comments-loaded', {
                    workItemId,
                    comments: this.sortedComments,
                    commentCount: this.comments.length,
                    mentionCount
                });
                this.$emit('comments-updated', {
                    workItemId,
                    commentCount: this.comments.length,
                    mentionCount
                });

                if (this.comments.length > 0) {
                    const lastId = Math.max(...this.comments.map(c => c.id || 0));
                    this.$http.post('/Inbox/MarkSeen', {
                        workItemId,
                        lastCommentId: lastId
                    }).then(() => {
                        this.$root.$emit('inbox-summary-updated');
                    }).catch(() => {});
                }
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) {
                    return;
                }
                this.toastError('Failed to load comments', err?.response?.data || err.message);
            } finally {
                this.loadingComments = false;
            }
        },
        onUpgradeClick() {
            this.$bvModal.hide('modal-work-item-comments');
            if (this.$route.path !== '/plans') {
                this.$router.push({
                    path: '/plans',
                    query: {
                        reason: 'Replying to discussions',
                        requiredPlan: 'Pro Plan'
                    }
                }).catch(() => {});
            }
        },
        async submitComment() {
            if (this.isViewOnlyMode) {
                this.toastUpgradeRequired('Pro Plan', 'Replying to discussions requires the Pro Plan.');
                return;
            }
            const rawText = (this.newCommentText || '').trim();
            if (!rawText || !this.activeItem || this.postingComment) return;

            let formattedText = rawText;

            const candidateMap = new Map();
            [...this.knownMentions, ...(this.teamMembers || [])].forEach(m => {
                if (m && m.displayName && m.id && !candidateMap.has(m.displayName.toLowerCase())) {
                    candidateMap.set(m.displayName.toLowerCase(), m);
                }
            });

            const candidates = Array.from(candidateMap.values())
                .sort((a, b) => (b.displayName.length - a.displayName.length));

            for (const member of candidates) {
                const cleanId = (member.id || '').replace(/^\{|\}$/g, '');
                const escaped = member.displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(^|\\s)@(${escaped})(?=\\s|$|[.,!?;:])`, 'gi');
                formattedText = formattedText.replace(regex, (match, prefix) => {
                    return `${prefix}<a href="#" data-vss-mention="version:2.0,{${cleanId}}">@${member.displayName}</a>`;
                });
            }

            this.postingComment = true;
            try {
                const resp = await this.$http.post('/TaskManagement/AddWorkItemComment', {
                    workItemId: this.activeItem.workItemId,
                    text: formattedText
                });

                const created = resp.data || {
                    id: Date.now(),
                    workItemId: this.activeItem.workItemId,
                    authorName: 'Me',
                    createdDate: new Date().toISOString(),
                    text: formattedText,
                    mentionsMe: false,
                    isFromCurrentUser: true
                };

                this.comments.unshift(created);
                this.newCommentText = '';
                this.knownMentions = [];
            this.editingCommentId = null;
            this.editCommentDraft = '';
            this.savingCommentEdit = false;
            this.confirmingDeleteCommentId = null;
            this.deletingComment = false;
                const newCount = this.comments.length;
                const mentionCount = this.comments.filter(c => c.mentionsMe).length;
                this.$set(this.activeItem, 'commentCount', newCount);
                this.$set(this.activeItem, 'mentionCount', mentionCount);
                this.$emit('comment-added', {
                    workItemId: this.activeItem.workItemId,
                    commentCount: newCount,
                    mentionCount,
                    comment: created
                });
                this.$emit('comments-updated', {
                    workItemId: this.activeItem.workItemId,
                    commentCount: newCount,
                    mentionCount
                });
                this.toastSuccess('Comment posted', 'Discussion updated.');
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) {
                    return;
                }
                const msg = err?.response?.data?.message || err?.response?.data || err.message || 'Failed to post comment.';
                this.toastError('Failed to post comment', typeof msg === 'string' ? msg : JSON.stringify(msg));
            } finally {
                this.postingComment = false;
            }
        },
        isAuthoredByCurrentUser(comment) {
            if (!comment) return false;
            if (comment.isFromCurrentUser) return true;
            const authorName = String(comment.authorName || '').toLowerCase().trim();
            const authorEmail = String(comment.authorEmail || '').toLowerCase().trim();
            const account = this.account || this.$store?.state?.account || {};
            const myName = String(account.fullName || '').toLowerCase().trim();
            const myEmail = String(account.email || '').toLowerCase().trim();
            if (myName && authorName === myName) return true;
            if (myEmail && (authorEmail === myEmail || authorName === myEmail)) return true;
            const currentMember = (this.teamMembers || []).find(m => m && m.isCurrentUser);
            if (currentMember) {
                const memName = String(currentMember.displayName || '').toLowerCase().trim();
                const memEmail = String(currentMember.uniqueName || '').toLowerCase().trim();
                if (memName && authorName === memName) return true;
                if (memEmail && (authorEmail === memEmail || authorName === memEmail)) return true;
            }
            if ((this.isDemo || myEmail.endsWith('@demo.velocytrack.com')) && authorName === 'alex morgan') {
                return true;
            }
            return false;
        },
        stripHtmlForEdit(html) {
            if (!html) return '';
            return String(html)
                .replace(/<a\s+[^>]*data-vss-mention[^>]*>([\s\S]*?)<\/a>/gi, '$1')
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
                .replace(/<[^>]+>/g, '')
                .replace(/&nbsp;/gi, ' ')
                .replace(/&amp;/gi, '&')
                .replace(/&lt;/gi, '<')
                .replace(/&gt;/gi, '>')
                .replace(/&quot;/gi, '"')
                .trim();
        },
        formatMentionsInText(rawText) {
            let formattedText = (rawText || '').trim();
            const candidateMap = new Map();
            [...this.knownMentions, ...(this.teamMembers || [])].forEach(m => {
                if (m && m.displayName && m.id && !candidateMap.has(m.displayName.toLowerCase())) {
                    candidateMap.set(m.displayName.toLowerCase(), m);
                }
            });
            const candidates = Array.from(candidateMap.values())
                .sort((a, b) => (b.displayName.length - a.displayName.length));
            for (const member of candidates) {
                const cleanId = (member.id || '').replace(/^\{|\}$/g, '');
                const escaped = member.displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(^|\\s)@(${escaped})(?=\\s|$|[.,!?;:])`, 'gi');
                formattedText = formattedText.replace(regex, (match, prefix) => {
                    return `${prefix}<a href="#" data-vss-mention="version:2.0,{${cleanId}}">@${member.displayName}</a>`;
                });
            }
            return formattedText;
        },
        startEditComment(comment) {
            if (!comment || this.isViewOnlyMode) return;
            this.confirmingDeleteCommentId = null;
            this.editingCommentId = comment.id;
            this.editCommentDraft = this.stripHtmlForEdit(comment.text);
            this.$nextTick(() => {
                const refs = this.$refs.editCommentTextarea;
                const el = Array.isArray(refs) ? refs[0] : refs;
                const domEl = el && el.$el ? el.$el : el;
                if (domEl && typeof domEl.focus === 'function') {
                    domEl.focus();
                }
            });
        },
        cancelEditComment() {
            this.editingCommentId = null;
            this.editCommentDraft = '';
            this.savingCommentEdit = false;
        },
        onEditCommentKeydown(event, comment) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.cancelEditComment();
                return;
            }
            if (event.ctrlKey && event.key === 'Enter') {
                event.preventDefault();
                this.saveEditComment(comment);
            }
        },
        async saveEditComment(comment) {
            if (!comment || !this.activeItem || this.savingCommentEdit) return;
            const rawText = (this.editCommentDraft || '').trim();
            if (!rawText) return;

            const formattedText = this.formatMentionsInText(rawText);
            this.savingCommentEdit = true;
            try {
                const resp = await this.$http.post('/TaskManagement/UpdateWorkItemComment', {
                    workItemId: this.activeItem.workItemId,
                    commentId: comment.id,
                    text: formattedText
                });
                const updated = resp?.data;
                const newText = (updated && updated.text) ? updated.text : formattedText;
                this.$set(comment, 'text', newText);
                this.$set(comment, 'mentionsMe', !!(updated?.mentionsMe || this.isMentionToCurrentUser(newText)));
                const mentionCount = this.comments.filter(c => c.mentionsMe).length;
                this.$set(this.activeItem, 'mentionCount', mentionCount);
                const sortedAfterEdit = this.sortedComments;
                const latestAfterEdit = sortedAfterEdit.length > 0 ? sortedAfterEdit[0] : null;
                this.$set(this.activeItem, 'latestComment', latestAfterEdit);
                this.$emit('comments-updated', {
                    workItemId: this.activeItem.workItemId,
                    commentCount: this.comments.length,
                    mentionCount,
                    comment,
                    latestComment: latestAfterEdit,
                    comments: sortedAfterEdit
                });
                this.editingCommentId = null;
                this.editCommentDraft = '';
                this.toastSuccess('Comment updated', 'Discussion comment updated.');
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                const msg = err?.response?.data?.message || err?.response?.data || err.message || 'Failed to update comment.';
                this.toastError('Failed to update comment', typeof msg === 'string' ? msg : JSON.stringify(msg));
            } finally {
                this.savingCommentEdit = false;
            }
        },
        promptDeleteComment(comment) {
            if (!comment || this.isViewOnlyMode) return;
            this.editingCommentId = null;
            this.editCommentDraft = '';
            this.confirmingDeleteCommentId = comment.id;
        },
        cancelDeleteComment() {
            this.confirmingDeleteCommentId = null;
            this.deletingComment = false;
        },
        async confirmDeleteComment(comment) {
            if (!comment || !this.activeItem || this.deletingComment) return;
            this.deletingComment = true;
            try {
                await this.$http.post('/TaskManagement/DeleteWorkItemComment', {
                    workItemId: this.activeItem.workItemId,
                    commentId: comment.id
                });
                this.comments = this.comments.filter(c => c.id !== comment.id);
                const newCount = this.comments.length;
                const mentionCount = this.comments.filter(c => c.mentionsMe).length;
                const sortedAfterDelete = this.sortedComments;
                const latestAfterDelete = sortedAfterDelete.length > 0 ? sortedAfterDelete[0] : null;
                this.$set(this.activeItem, 'commentCount', newCount);
                this.$set(this.activeItem, 'mentionCount', mentionCount);
                this.$set(this.activeItem, 'latestComment', latestAfterDelete);
                this.$emit('comment-deleted', {
                    workItemId: this.activeItem.workItemId,
                    commentId: comment.id,
                    commentCount: newCount,
                    mentionCount,
                    latestComment: latestAfterDelete,
                    comments: sortedAfterDelete
                });
                this.$emit('comments-updated', {
                    workItemId: this.activeItem.workItemId,
                    commentCount: newCount,
                    mentionCount,
                    latestComment: latestAfterDelete,
                    comments: sortedAfterDelete
                });
                this.confirmingDeleteCommentId = null;
                this.toastSuccess('Comment deleted', 'Discussion comment removed.');
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                const msg = err?.response?.data?.message || err?.response?.data || err.message || 'Failed to delete comment.';
                this.toastError('Failed to delete comment', typeof msg === 'string' ? msg : JSON.stringify(msg));
            } finally {
                this.deletingComment = false;
            }
        },
        formatCommentDate(dateStr) {
            if (!dateStr) return '';
            try {
                const d = new Date(dateStr);
                const now = new Date();
                const diffMs = now - d;
                const diffMins = Math.floor(diffMs / 60000);
                if (diffMins < 1) return 'just now';
                if (diffMins < 60) return `${diffMins}m ago`;
                const diffHours = Math.floor(diffMins / 60);
                if (diffHours < 24) return `${diffHours}h ago`;
                const diffDays = Math.floor(diffHours / 24);
                if (diffDays < 7) return `${diffDays}d ago`;
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            } catch (_) {
                return dateStr;
            }
        },
        getInitials(name) {
            if (!name) return 'U';
            const parts = name.trim().split(/\s+/);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return parts[0].slice(0, 2).toUpperCase();
        },
        onAvatarError(e, member) {
            if (member) {
                this.$set(member, 'avatarFailed', true);
            } else if (e && e.target) {
                e.target.style.display = 'none';
            }
        },
        getAvatarBgStyle(name) {
            const adoColors = [
                '#0078D4', // Azure Blue
                '#107C41', // Teams Green
                '#5C2D91', // Purple
                '#D83B01', // Orange
                '#008272', // Teal
                '#B4009E', // Magenta
                '#004E8C', // Navy
                '#498205', // Olive
                '#8764B8', // Lavender
                '#C239B3'  // Pink
            ];
            let hash = 0;
            const str = name || 'User';
            for (let i = 0; i < str.length; i++) {
                hash = (hash << 5) - hash + str.charCodeAt(i);
                hash |= 0;
            }
            const idx = Math.abs(hash) % adoColors.length;
            return {
                backgroundColor: adoColors[idx],
                color: '#ffffff'
            };
        },
        getCaretPos(element, position) {
            if (!element) return { top: 0, left: 0, lineHeight: 22 };

            const properties = [
                'direction', 'boxSizing', 'width', 'overflowX', 'overflowY',
                'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
                'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
                'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
                'fontSizeAdjust', 'lineHeight', 'fontFamily', 'textAlign', 'textTransform',
                'textIndent', 'textDecoration', 'letterSpacing', 'wordSpacing'
            ];

            const div = document.createElement('div');
            div.style.position = 'absolute';
            div.style.top = '-9999px';
            div.style.left = '-9999px';
            div.style.visibility = 'hidden';
            div.style.whiteSpace = 'pre-wrap';
            div.style.wordWrap = 'break-word';

            const computed = window.getComputedStyle(element);
            properties.forEach(prop => {
                div.style[prop] = computed[prop];
            });

            const text = element.value || '';
            div.textContent = text.substring(0, position);

            const span = document.createElement('span');
            span.textContent = text.substring(position, position + 1) || ' ';
            div.appendChild(span);

            document.body.appendChild(div);

            const top = span.offsetTop + parseInt(computed.borderTopWidth || '0', 10);
            const left = span.offsetLeft + parseInt(computed.borderLeftWidth || '0', 10);
            const lineHeight = parseInt(computed.lineHeight || '22', 10) || 22;

            document.body.removeChild(div);
            return { top, left, lineHeight };
        },
        updateDropdownPosition() {
            const el = this.getTextareaEl();
            if (!el) return;
            const cursorPos = el.selectionStart || 0;
            const atIdx = Math.max(0, this.mentionStartIndex >= 0 ? this.mentionStartIndex : cursorPos);
            const pos = this.getCaretPos(el, atIdx);

            const textareaWidth = el.clientWidth || 400;
            const dropdownWidth = 320;

            const top = Math.max(0, pos.top - el.scrollTop + pos.lineHeight + 6);
            const maxLeft = Math.max(0, textareaWidth - dropdownWidth);
            const left = Math.max(0, Math.min(pos.left, maxLeft));

            this.caretTop = top;
            this.caretLeft = left;
        },
        isMentionToCurrentUser(mentionText, mentionAttrs = '') {
            if (!mentionText) return false;
            const text = String(mentionText).replace(/<[^>]+>/g, '').replace(/^@/, '').trim().toLowerCase();
            if (!text) return false;
            const attrs = String(mentionAttrs || '').toLowerCase();

            const currentMember = (this.teamMembers || []).find(m => m && m.isCurrentUser);
            const account = this.account || this.$store?.state?.account || {};
            const accountFullName = (account.fullName || '').toLowerCase().trim();
            const accountEmail = (account.email || '').toLowerCase().trim();
            const memberDisplayName = (currentMember?.displayName || '').toLowerCase().trim();
            const memberUniqueName = (currentMember?.uniqueName || '').toLowerCase().trim();
            const currentUserId = (currentMember?.id || account.uid || '').toLowerCase().trim();

            // 1. Direct ID match from data-vss-mention attribute if present
            if (currentUserId && attrs.includes(currentUserId)) {
                return true;
            }

            // 2. Exact match against full display names, unique names, or email
            const exactCandidates = [accountFullName, memberDisplayName, accountEmail, memberUniqueName].filter(Boolean);
            if (exactCandidates.includes(text)) {
                return true;
            }

            // 3. Email prefix match (e.g. "jem.hasan" from "jem.hasan@nuvei.com")
            const emailPrefixes = [accountEmail, memberUniqueName]
                .filter(Boolean)
                .map(e => e.split('@')[0])
                .filter(p => p && p.length >= 3);
            if (emailPrefixes.includes(text)) {
                return true;
            }

            // 4. Token matching on full name (e.g. "Jem Hasan" or "Dzhem Hasan")
            const fullNames = [accountFullName, memberDisplayName].filter(Boolean);
            for (const name of fullNames) {
                const parts = name.split(/\s+/).filter(p => p.length >= 2);
                if (parts.length >= 2) {
                    const first = parts[0];
                    const last = parts[parts.length - 1];
                    if (text.includes(' ')) {
                        if (text.includes(first) && text.includes(last)) {
                            return true;
                        }
                    } else {
                        if (text === first && first.length >= 3) {
                            return true;
                        }
                        if (text === `${first[0]}${last}` || text === `${first}.${last}`) {
                            return true;
                        }
                    }
                } else if (parts.length === 1 && text === parts[0]) {
                    return true;
                }
            }

            // 5. Check transliteration variants for Dzhem / Jem
            const isJemAccount = fullNames.some(n => n.includes('jem') || n.includes('dzhem')) ||
                                 emailPrefixes.some(p => p.includes('jem') || p.includes('dhasan') || p.includes('jhasan'));
            if (isJemAccount) {
                if (text.includes('jem hasan') || text.includes('dzhem hasan') || text === 'jem' || text === 'dzhem' || text === 'dhasan' || text === 'jhasan') {
                    return true;
                }
            }

            return false;
        },
        renderCommentText(text, mentionsMe) {
            if (!text) return '';
            const clean = DOMPurify.sanitize(text, {
                ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'em', 'strong', 'a', 'span', 'code', 'pre', 'ul', 'ol', 'li', 'div', 'blockquote'],
                ALLOWED_ATTR: ['href', 'target', 'class', 'data-vss-mention', 'rel']
            });

            let html = clean;
            if (!html.includes('<p>') && !html.includes('<div>') && !html.includes('<br')) {
                html = html.replace(/\n/g, '<br/>');
            }

            html = html.replace(/<a\s+([^>]*data-vss-mention[^>]*)>([\s\S]*?)<\/a>/gi, (match, attrs, p1) => {
                const isMe = mentionsMe && this.isMentionToCurrentUser(p1, attrs);
                return `<span class="mention-pill ${isMe ? 'mention-to-me' : ''}">${p1}</span>`;
            });

            html = html.replace(/(^|\s)@([a-zA-Z0-9_.-]+(?:\s+[a-zA-Z0-9_.-]+)?)/g, (match, prefix, name) => {
                const isMe = mentionsMe && this.isMentionToCurrentUser(name);
                return `${prefix}<span class="mention-pill ${isMe ? 'mention-to-me' : ''}">@${name}</span>`;
            });

            return DOMPurify.sanitize(html, {
                ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'em', 'strong', 'a', 'span', 'code', 'pre', 'ul', 'ol', 'li', 'div', 'blockquote'],
                ALLOWED_ATTR: ['href', 'target', 'class', 'rel']
            });
        },
        onModalHidden() {
            this.activeItem = null;
            this.comments = [];
            this.newCommentText = '';
            this.showMentionDropdown = false;
            this.knownMentions = [];
            this.editingCommentId = null;
            this.editCommentDraft = '';
            this.savingCommentEdit = false;
            this.confirmingDeleteCommentId = null;
            this.deletingComment = false;
            this.searchingIdentities = false;
            if (this.identitySearchTimer) {
                clearTimeout(this.identitySearchTimer);
                this.identitySearchTimer = null;
            }
        },
        actionHoverPop(title) {
            return {
                title,
                container: 'body',
                offset: '0, 8',
                customClass: 'action-hover-pop modal-hover-pop',
                boundary: 'window'
            };
        },
        dismissModalStatus() {
            if (!this.activeItem) return;
            const lastCommentId = (this.comments && this.comments.length > 0)
                ? Math.max(...this.comments.map(c => c.id || 0))
                : (this.activeItem.latestComment?.id || this.activeItem.lastCommentId || 0);

            this.$store.commit('dismissDiscussion', {
                workItemId: this.activeItem.workItemId,
                lastCommentId
            });

            this.$set(this.activeItem, 'discussionStatus', 'NotActionable');
            this.$set(this.activeItem, 'isAnswered', true);

            this.$emit('status-dismissed', {
                workItemId: this.activeItem.workItemId,
                lastCommentId
            });

            this.toastSuccess('Label dismissed', `Marked discussion #${this.activeItem.workItemId} as resolved in person/meeting.`);
        }
    },
    beforeUnmount() {
        if (this.identitySearchTimer) {
            clearTimeout(this.identitySearchTimer);
            this.identitySearchTimer = null;
        }
    }
};
</script>

<style scoped>
::v-deep .modal-dialog {
    max-width: 1200px;
}

@media (min-width: 1400px) {
    ::v-deep .modal-dialog {
        max-width: 1350px;
    }
}

.wi-modal-title {
    color: #0f172a;
    font-size: 1.05rem;
    min-width: 0;
}

[data-theme="dark"] .wi-modal-title {
    color: #f8fafc;
}

.mention-typeahead-dropdown {
    position: absolute;
    width: 320px;
    max-width: 95%;
    background-color: #ffffff;
    border: 1.5px solid #3b82f6;
    border-radius: 8px;
    z-index: 1060;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden; /* Strictly no scroll container */
}

[data-theme="dark"] .mention-typeahead-dropdown {
    background-color: #1e293b;
    border-color: #3b82f6;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(59, 130, 246, 0.4);
}

.mention-list-container {
    overflow: hidden; /* No scroll container */
    max-height: none;
}

.mention-member-item {
    cursor: pointer;
    transition: background-color 0.12s, color 0.12s;
    user-select: none;
    border-left: 3px solid transparent;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.mention-member-item:last-child {
    border-bottom: none;
}

[data-theme="dark"] .mention-member-item {
    border-bottom-color: rgba(255, 255, 255, 0.05);
}

/* Teammate (Mate) results distinction */
.mention-member-item.is-team-mate {
    background-color: rgba(13, 110, 253, 0.06);
    border-left-color: rgba(13, 110, 253, 0.4);
}

[data-theme="dark"] .mention-member-item.is-team-mate {
    background-color: rgba(59, 130, 246, 0.12);
    border-left-color: rgba(96, 165, 250, 0.5);
}

.mention-member-item:hover,
.mention-member-item.is-selected {
    background-color: #eff6ff;
    border-left-color: #2563eb;
}

.mention-member-item.is-team-mate:hover,
.mention-member-item.is-team-mate.is-selected {
    background-color: #e0edff;
    border-left-color: #2563eb;
}

[data-theme="dark"] .mention-member-item:hover,
[data-theme="dark"] .mention-member-item.is-selected {
    background-color: #2563eb;
    border-left-color: #60a5fa;
}

[data-theme="dark"] .mention-member-item.is-team-mate:hover,
[data-theme="dark"] .mention-member-item.is-team-mate.is-selected {
    background-color: #1d4ed8;
    border-left-color: #93c5fd;
}

.mention-avatar-wrapper {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
}

.mention-member-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.72rem;
    font-weight: 700;
    flex-shrink: 0;
    object-fit: cover;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
}

.member-name {
    color: #0f172a;
    font-weight: 600;
    font-size: 0.85rem;
    line-height: 1.25;
}

.member-handle {
    color: #64748b;
    font-size: 0.73rem;
    line-height: 1.2;
}

.team-mate-badge {
    font-size: 0.68rem;
    font-weight: 600;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background-color: rgba(13, 110, 253, 0.12);
    color: #0d6efd;
    border: 1px solid rgba(13, 110, 253, 0.3);
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
}

[data-theme="dark"] .team-mate-badge {
    background-color: rgba(96, 165, 250, 0.2);
    color: #93c5fd;
    border-color: rgba(96, 165, 250, 0.4);
}

.mention-member-item.is-selected .team-mate-badge,
.mention-member-item:hover .team-mate-badge {
    background-color: #2563eb;
    color: #ffffff;
    border-color: #1d4ed8;
}

[data-theme="dark"] .mention-member-item.is-selected .team-mate-badge,
[data-theme="dark"] .mention-member-item:hover .team-mate-badge {
    background-color: rgba(255, 255, 255, 0.25);
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.4);
}

[data-theme="dark"] .member-name {
    color: #f8fafc;
}

[data-theme="dark"] .member-handle {
    color: #94a3b8;
}

.mention-member-item.is-selected .member-name,
.mention-member-item:hover .member-name {
    color: #1d4ed8;
}

.mention-member-item.is-selected .member-handle,
.mention-member-item:hover .member-handle {
    color: #2563eb;
}

[data-theme="dark"] .mention-member-item.is-selected .member-name,
[data-theme="dark"] .mention-member-item:hover .member-name {
    color: #ffffff;
}

[data-theme="dark"] .mention-member-item.is-selected .member-handle,
[data-theme="dark"] .mention-member-item:hover .member-handle {
    color: rgba(255, 255, 255, 0.85);
}

.wi-reply-composer {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.wi-reply-composer:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

[data-theme="dark"] .wi-reply-composer {
    background-color: #1e2633;
    border-color: #334155;
}

[data-theme="dark"] .wi-reply-composer:focus-within {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.18);
}

[data-theme="dark"] .wi-reply-composer textarea {
    background-color: #0f172a;
    border-color: #334155;
    color: #f8fafc;
}

[data-theme="dark"] .wi-reply-composer textarea:focus {
    background-color: #0f172a;
    border-color: #60a5fa;
    color: #f8fafc;
}

.wi-comments-header-icon ::v-deep svg,
.wi-comments-header-icon ::v-deep img {
    width: 16px;
    height: 16px;
    vertical-align: middle;
    object-fit: contain;
}

.comment-avatar {
    width: 32px;
    height: 32px;
}

.avatar-img {
    width: 32px;
    height: 32px;
    object-fit: cover;
}

.avatar-fallback {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #007bff, #6610f2);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.comment-bubble {
    background-color: #f8f9fa;
    border-color: #e9ecef !important;
    transition: border-color 0.2s, background-color 0.2s;
}

.comment-bubble.has-mention-to-me {
    border-left: 3px solid #ffc107 !important;
    background-color: rgba(255, 193, 7, 0.06);
}

[data-theme="dark"] .comment-bubble {
    background-color: #2b3035;
    border-color: #3e444a !important;
}

[data-theme="dark"] .comment-bubble.has-mention-to-me {
    border-left: 3px solid #ffc107 !important;
    background-color: rgba(255, 193, 7, 0.09);
}

.comment-content ::v-deep .mention-pill {
    display: inline-block;
    padding: 0.05rem 0.4rem;
    border-radius: 999px;
    font-size: 0.85em;
    font-weight: 600;
    background: rgba(13, 110, 253, 0.12);
    color: #0d6efd;
    margin: 0 0.15rem;
}

[data-theme="dark"] .comment-content ::v-deep .mention-pill {
    background: rgba(13, 110, 253, 0.25);
    color: #6ea8fe;
}

.comment-content ::v-deep .mention-pill.mention-to-me {
    background: rgba(255, 193, 7, 0.22);
    color: #856404;
    border: 1px solid rgba(255, 193, 7, 0.5);
}

[data-theme="dark"] .comment-content ::v-deep .mention-pill.mention-to-me {
    background: rgba(255, 193, 7, 0.25);
    color: #ffda6a;
    border: 1px solid rgba(255, 193, 7, 0.5);
}

.comment-author-name {
    color: #333;
}

[data-theme="dark"] .comment-author-name {
    color: #e2e8f0;
}

.comment-content {
    color: #495057;
    word-break: break-word;
    line-height: 1.45;
}

[data-theme="dark"] .comment-content {
    color: #cbd5e1;
}

.empty-comments-card {
    background: rgba(0, 0, 0, 0.02);
    border-radius: 6px;
    margin: 1rem 0;
}

[data-theme="dark"] .empty-comments-card {
    background: rgba(255, 255, 255, 0.02);
}

.extra-small {
    font-size: 0.72rem;
}

.comment-textarea {
    font-size: 0.85rem;
}

.badge-dismiss-btn {
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    line-height: 1;
    font-size: 0.9rem;
    color: inherit;
    opacity: 0.7;
    cursor: pointer;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s, background-color 0.15s;
    width: 14px;
    height: 14px;
}

.badge-dismiss-btn:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.2);
}

[data-theme="dark"] .badge-dismiss-btn:hover {
    background-color: rgba(255, 255, 255, 0.25);
}
.comment-action-btn {
    font-size: 0.72rem;
    line-height: 1.1;
    text-decoration: none !important;
}

.comment-action-btn:hover {
    text-decoration: underline !important;
}

.comment-delete-confirm-box {
    background-color: rgba(220, 53, 69, 0.08);
}

[data-theme="dark"] .comment-delete-confirm-box {
    background-color: rgba(220, 53, 69, 0.16);
}
</style>
